import { memo, ReactNode, useCallback, useMemo } from 'react';
import { FormField } from '@amzn/awsui-components-core';
import {
  TiptapEditor,
  convertStringToTiptapOutput,
  getTextWithEntityPlaceholders,
} from '@amzn/awscat-editor';

import CharCountIndicator from '../v2-common/CharCountIndicator';
import styles from '../v2-common/CompactTiptapEditorStyles.module.scss';
import DuplicateCheckResultView from '../v2-common/DuplicateCheckResultView';
import { TitleFieldViewModel } from './ContentTitleHeadingViewModel';

// Stable references to prevent unnecessary re-renders of TiptapEditor.
const EMPTY_MENU_ITEMS: never[] = [];
const OUTPUT_FORMATS: ('json' | 'text')[] = ['json', 'text'];

interface TitleFieldViewProps {
  readonly vm: TitleFieldViewModel;
  readonly label: ReactNode;
  readonly description: string;
  readonly min: number;
  readonly max: number;
  readonly entities: Record<string, any>;
  readonly showDuplicateCheck?: boolean;
  readonly isOptional?: boolean;
  readonly testId?: string;
}

const TitleFieldView = memo(function TitleFieldView({
  vm,
  label,
  description,
  min,
  max,
  entities,
  showDuplicateCheck = true,
  isOptional = false,
  testId,
}: TitleFieldViewProps) {
  const initialContent = useMemo(
    () => convertStringToTiptapOutput(vm.value),
    // Passing an empty array here to make sure convertStringToTiptapOutput()
    // called exactly ONCE to take a snapshot of initial `value`
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleChange = useCallback(
    (_editor: unknown, output: Record<string, any>) => {
      vm.handleChange(getTextWithEntityPlaceholders(output.json));
    },
    [vm],
  );

  return (
    <FormField
      label={label}
      description={description}
      errorText={vm.validationError}
      stretch={true}
    >
      <div
        className={`${styles.container} ${vm.validationError ? styles.containerError : ''}`}
        onBlur={vm.handleBlur}
        data-testid={testId}
      >
        <TiptapEditor
          content={initialContent}
          editable
          singleLine
          removeFormatting
          hideMenuBar
          hideFooter
          menuItems={EMPTY_MENU_ITEMS}
          entities={entities}
          outputFormats={OUTPUT_FORMATS}
          onChange={handleChange}
          useContainer={false}
        />
      </div>
      <CharCountIndicator
        convertedLength={vm.convertedLength}
        min={min}
        max={max}
        isValidationErrorVisible={!!vm.validationError}
        isOptional={isOptional}
      />
      {showDuplicateCheck && vm.value && vm.convertedLength > 0 && (
        <DuplicateCheckResultView
          isSearching={vm.isSearchingDuplicates}
          duplicates={vm.duplicates}
          isCheckFailed={vm.isDuplicateCheckFailed}
        />
      )}
    </FormField>
  );
});

export default TitleFieldView;
